import MarketingConsent from "@/components/MarketingConsent";
import { headingPrimary } from "@/constants";
import type { LoginMethod } from "@/context/PassportProvider";
import { cn } from "@/lib/utils";
import { onDarkBase } from "@biom3/design-tokens";
import { Body, Box, ButtCon, TextInput } from "@biom3/react";
import { ArrowRight, TriangleAlert } from "lucide-react";
import { useCallback, useState } from "react";
import { z } from "zod";

interface SignUpButtonsProps {
  buttonText?: string;
  onLogin: (method?: LoginMethod) => void;
}

export const SignUpButtons = ({ buttonText, onLogin }: SignUpButtonsProps) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!email) return setEmailError("Email is required");

      if (z.string().email().safeParse(email).error)
        return setEmailError("Invalid email address");

      onLogin({ email });
    },
    [email, onLogin],
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setEmailError(null);
    },
    [],
  );

  const handleClearEmail = useCallback(() => {
    setEmail("");
    setEmailError(null);
  }, []);

  return (
    <div
      data-testid="reward-login-buttons-unified"
      className="w-full flex flex-col items-center justify-center"
    >
      {buttonText ? (
        <Body
          style={{
            textAlign: "center",
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: headingPrimary.style.fontFamily,
            fontSize: "clamp(16px, 2.5dvh, 20px)",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "var(--text-heading-small-bold-lineheight, 28px)",
            color: "#A3CAF3",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: onDarkBase.spacing.x1,
            marginBottom: "6px",
          }}
        >
          <img
            src="/images/arrow.svg"
            alt="arrow"
            width={30}
            height={30}
            style={{
              verticalAlign: "middle",
              position: "relative",
              top: "8px",
            }}
          />
          {buttonText}
        </Body>
      ) : null}
      <div
        id="actions"
        data-testid="login_actions"
        className="items-center flex flex-col gap-y-2"
      >
        <div className="flex flex-col items-center w-full space-y-2">
          <div className="flex justify-center items-center gap-x-6">
            <ButtCon
              size={["medium", "large"]}
              onClick={() => onLogin("google")}
              aria-label="Google login"
            >
              <svg viewBox="0 0 40 40" width="40px">
                <title>Google</title>
                <path
                  d="M29.6 20.2273C29.6 19.5182 29.5364 18.8364 29.4182 18.1818H20V22.05H25.3818C25.15 23.3 24.4455 24.3591 23.3864 25.0682V27.5773H26.6182C28.5091 25.8364 29.6 23.2727 29.6 20.2273Z"
                  fill="#4285F4"
                />
                <path
                  d="M20 30C22.7 30 24.9636 29.1045 26.6181 27.5773L23.3863 25.0682C22.4909 25.6682 21.3454 26.0227 20 26.0227C17.3954 26.0227 15.1909 24.2636 14.4045 21.9H11.0636V24.4909C12.7091 27.7591 16.0909 30 20 30Z"
                  fill="#34A853"
                />
                <path
                  d="M14.4045 21.9C14.2045 21.3 14.0909 20.6591 14.0909 20C14.0909 19.3409 14.2045 18.7 14.4045 18.1V15.5091H11.0636C10.3864 16.8591 10 18.3864 10 20C10 21.6136 10.3864 23.1409 11.0636 24.4909L14.4045 21.9Z"
                  fill="#FBBC04"
                />
                <path
                  d="M20 13.9773C21.4681 13.9773 22.7863 14.4818 23.8227 15.4727L26.6909 12.6045C24.9591 10.9909 22.6954 10 20 10C16.0909 10 12.7091 12.2409 11.0636 15.5091L14.4045 18.1C15.1909 15.7364 17.3954 13.9773 20 13.9773Z"
                  fill="#E94235"
                />
              </svg>
            </ButtCon>
            <ButtCon
              icon="Apple"
              onClick={() => onLogin("apple")}
              size={["medium", "large"]}
              aria-label="Apple login"
            />
          </div>
          <p className="text-sm">or</p>
          <form className="w-full space-y-1 text-start" onSubmit={handleSubmit}>
            <TextInput
              sizeVariant="large"
              className={cn(
                "transition-all duration-300 rounded-lg !h-12 text-sm",
                emailError && "ring-2 ring-red-500",
              )}
              placeholder="Enter email address"
              onChange={handleEmailChange}
              value={email}
              onClearValue={handleClearEmail}
              name="email"
              autoComplete="email"
              hideClearValueButton
            >
              <TextInput.Icon icon="EmailAddress" />
              <TextInput.Button type="submit" className="size-8">
                <ArrowRight className="size-4" />
              </TextInput.Button>
            </TextInput>
          </form>
          <p
            className={cn(
              "text-sm h-5 text-red-500 flex items-center gap-x-2 pl-4 transition-opacity duration-300 place-self-start",
              emailError ? "opacity-100" : "opacity-0",
            )}
          >
            <TriangleAlert className="size-4" /> {emailError}
          </p>
        </div>

        <div className="flex flex-col items-center justify-center w-full text-left">
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <span className="text-[#B6B6B6] text-xs text-left">
              By continuing, you agree to our{" "}
              <a
                href="https://www.immutable.com/terms-of-service"
                target="_blank"
                rel="noreferrer"
                data-testid="terms"
              >
                Terms of Service
              </a>
              , and acknowledge you have understood our{" "}
              <a
                href="https://www.immutable.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://www.immutable.com/collection-statement"
                target="_blank"
                rel="noreferrer"
              >
                Collection Statement
              </a>
            </span>
          </Box>

          <MarketingConsent variant="concise" />
        </div>
      </div>
    </div>
  );
};
